import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Bell,
  Twitter,
  Plus,
  Facebook,
  Instagram,
  Youtube,
  Linkedin,
  Save,
  Lock,
  Palette,
  Globe,
  Shield,
  AlertCircle,
  CheckCircle,
  X,
  Brush,
  LogOut
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTheme } from '../lib/theme';
import { startYoutubeOAuth } from '../lib/youtube';
import { startFacebookOAuth } from '../lib/facebook';
import { startLinkedInOAuth } from '../lib/linkedin';
import { useNavigate } from 'react-router-dom';
import type { AccentColor } from '../lib/theme';
import AvatarDisplay from '../components/AvatarDisplay';
import AvatarModal from '../components/AvatarModal';
import type { AvatarData } from '../components/AvatarGenerator';
import { toast } from 'react-hot-toast';

// Generate a cryptographically secure code verifier
const generateCodeVerifier = (length = 128) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => 
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'[byte % 66]
  ).join('');
};

// Base64-URL encode an ArrayBuffer
const base64UrlEncode = (buffer: ArrayBuffer): string => {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// Generate PKCE code challenge from verifier
const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  try {
    // Convert the code verifier to UTF-8 bytes
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    
    // Generate SHA-256 hash of the code verifier
    const digest = await crypto.subtle.digest('SHA-256', data);
    
    // Base64-URL encode the hash to create the code challenge
    return base64UrlEncode(digest);
  } catch (error) {
    console.error('Error generating code challenge:', error);
    throw new Error('Failed to generate PKCE code challenge');
  }
};

// Generate a secure state parameter
const generateState = (): string => {
  try {
    // Generate 32 bytes (256 bits) of random data for the state
    const stateArray = new Uint8Array(32);
    crypto.getRandomValues(stateArray);
    
    // Convert to Base64URL format
    const state = base64UrlEncode(stateArray.buffer);
    
    // Verify the state is not empty
    if (!state) {
      throw new Error('Generated state is empty');
    }
    
    return state;
  } catch (error) {
    console.error('Error generating state parameter:', error);
    throw new Error('Failed to generate secure state parameter');
  }
};

const Settings = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [notifications, setNotifications] = useState({
    emailUpdates: true,
    contentAlerts: false,
    performanceReports: true
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const { theme, setTheme, accentColor, setAccentColor } = useTheme();
  const [advancedLayout, setAdvancedLayout] = useState(false);
  const [language, setLanguage] = useState('en');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [timezone, setTimezone] = useState('UTC');
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [avatarData, setAvatarData] = useState<AvatarData | undefined>(undefined);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [twitterConnecting, setTwitterConnecting] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [twitterAccount, setTwitterAccount] = useState<{
    id: string;
    username: string;
    access_token?: string;
  } | null>(null);
  const [socialAccounts, setSocialAccounts] = useState<Record<string, boolean>>({});
  const [webhookInfo, setWebhookInfo] = useState<{
    sent: boolean;
    data: any;
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadUserProfile();
    loadSocialAccounts();
    
    // Check if we're returning from Twitter OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('twitter_connected') === 'true') {
      // Get Twitter account info from local storage
      const twitterData = JSON.parse(localStorage.getItem('twitter_account_data') || 'null');
      if (twitterData) {
        setTwitterAccount(twitterData);
        // Send updated webhook with account data
        sendAccountInfoToWebhook('twitter', twitterData)
          .then(() => {
            localStorage.removeItem('twitter_account_data');
          })
          .catch(console.error);
      }
      setSuccessMessage('Twitter account connected successfully!');
      // Clean up the URL
      window.history.replaceState({}, document.title, '/settings');
    }
    
    // Check for error parameter
    if (urlParams.get('error')) {
      setError(decodeURIComponent(urlParams.get('error') || 'Unknown error'));
      window.history.replaceState({}, document.title, '/settings');
    }
  }, []);

  const loadSocialAccounts = async () => {
    try {
      const { data: accounts, error } = await supabase
        .from('social_accounts')
        .select('platform, connected');

      if (error) throw error;

      const accountStatus = accounts?.reduce((acc, account) => ({
        ...acc,
        [account.platform]: account.connected
      }), {});

      setSocialAccounts(accountStatus || {});
    } catch (err) {
      console.error('Error loading social accounts:', err);
    }
  };

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      console.log('Loading user profile...');
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting user:', userError);
        throw userError;
      }
      
      if (!user) {
        console.error('No user found');
        throw new Error('No user found');
      }

      console.log('User found:', user.id);
      setUser(user);
      setEmail(user.email || '');

      // Load profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*, avatar_data')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        throw profileError;
      }

      console.log('Profile loaded:', profile);
      
      // Set form data
      if (profile) {
        setName(profile.full_name || '');
        setAccentColor(profile.theme_color || 'indigo');
        setProfilePicture(profile.profile_picture_url || null);
        setPhoneNumber(profile.phone_number || '');
        if (profile.avatar_data) {
          setAvatarData(profile.avatar_data);
        }
      } else {
        // Create a new profile if one doesn't exist
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            full_name: user.user_metadata?.full_name || '',
            theme_color: 'indigo'
          });

        if (insertError) throw insertError;

        // Set default values
        setName(user.user_metadata?.full_name || '');
        setAccentColor('indigo');
        setProfilePicture(null);
        setPhoneNumber('');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAvatar = async (newAvatarData: AvatarData) => {
    try {
      setError(null);
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update profile with avatar data
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          avatar_data: newAvatarData,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setAvatarData(newAvatarData);
      setSuccessMessage('Avatar updated successfully');

    } catch (err) {
      console.error('Error saving avatar:', err);
      setError(err instanceof Error ? err.message : 'Failed to save avatar');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setError(null);
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image size must be less than 5MB');
      }

      setLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          profile_picture_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfilePicture(publicUrl);
      setSuccessMessage('Profile picture updated successfully');

    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload profile picture');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: name,
          phone_number: phoneNumber,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update email if changed
      if (email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: email
        });
        if (emailError) throw emailError;
      }

      setSuccessMessage('Settings updated successfully');
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectTwitter = async () => {
    try {
      setError(null);
      setSuccessMessage(null);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Delete the social account record
      const { error: deleteError } = await supabase
        .from('social_accounts')
        .delete()
        .eq('user_id', user.id)
        .eq('platform', 'twitter');

      if (deleteError) throw deleteError;

      // Update local state
      setSocialAccounts(prev => ({
        ...prev,
        twitter: false
      }));
      setTwitterAccount(null);
      setSuccessMessage('Twitter account disconnected successfully');

      // Send webhook about disconnection
      await sendAccountInfoToWebhook('twitter', {
        disconnected: true,
        timestamp: new Date().toISOString()
      });

    } catch (err) {
      console.error('Error disconnecting Twitter:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect Twitter account');
    }
  };

  const handleDisconnectLinkedIn = async () => {
    try {
      setError(null);
      setSuccessMessage(null);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Delete the social account record
      const { error: deleteError } = await supabase
        .from('social_accounts')
        .delete()
        .eq('user_id', user.id)
        .eq('platform', 'linkedin');

      if (deleteError) throw deleteError;

      // Update local state
      setSocialAccounts(prev => ({
        ...prev,
        linkedin: false
      }));
      setSuccessMessage('LinkedIn account disconnected successfully');

      // Send webhook about disconnection
      await sendAccountInfoToWebhook('linkedin', {
        disconnected: true,
        timestamp: new Date().toISOString()
      });

    } catch (err) {
      console.error('Error disconnecting LinkedIn:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect LinkedIn account');
    }
  };

  const handleTwitterConnect = async () => {
    try {
      setTwitterConnecting(true);
      setError(null);
      
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error('Not authenticated');
      }
      
      // Store user ID in cookie
      document.cookie = `user_id=${user.id}; path=/; secure; samesite=lax; max-age=3600`;
      
      // 1. Generate and store PKCE code verifier (128 bytes for maximum security)
      const codeVerifier = generateCodeVerifier();
      document.cookie = `code_verifier=${codeVerifier}; path=/; secure; samesite=lax; max-age=3600`;
      
      // 2. Generate cryptographically secure state parameter
      const state = generateState();
      document.cookie = `oauth_state=${state}; path=/; secure; samesite=lax; max-age=3600`;

      // 3. Generate code challenge from verifier
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      // 4. Get client ID from environment variables
      const clientId = import.meta.env.VITE_TWITTER_CLIENT_ID;
      if (!clientId) {
        throw new Error('Twitter client ID not found in environment variables');
      }
      
      // 5. Construct exact callback URL that matches Twitter app settings
      const redirectUri = 'https://serge.unleashsupport.com/.netlify/functions/twitterCallback';
      
      // 6. Define required scopes for Twitter API access
      const scope = 'tweet.read users.read offline.access';
      
      // 7. Construct Twitter's OAuth 2.0 authorization URL with all required parameters
      const authUrl = new URL('https://twitter.com/i/oauth2/authorize');
      
      // Add all required OAuth 2.0 parameters
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('client_id', clientId);
      authUrl.searchParams.append('redirect_uri', redirectUri);
      authUrl.searchParams.append('scope', scope);
      authUrl.searchParams.append('state', state);
      authUrl.searchParams.append('code_challenge', codeChallenge);
      authUrl.searchParams.append('code_challenge_method', 'S256');
      
      // Log OAuth parameters for debugging
      console.log('Starting Twitter OAuth flow:', {
        redirectUri,
        scope,
        state: state.substring(0, 10) + '...'
      });
      
      // 8. Redirect to Twitter's authorization page
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Error initiating Twitter OAuth flow:', err);
      setError(err instanceof Error ? err.message : 'Failed to start Twitter authentication');
      setTwitterConnecting(false);
    }
  };


  const handleConnect = (platform: string) => {
    // For Twitter, start OAuth flow directly
    if (platform === 'twitter') {
      handleTwitterConnect();
    } else if (platform === 'youtube') {
      startYoutubeOAuth().catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to start YouTube authentication');
      });
    } else if (platform === 'facebook') {
      startFacebookOAuth().catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to start Facebook authentication');
      });
    } else if (platform === 'linkedin') {
      startLinkedInOAuth().catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to start LinkedIn authentication');
      });
    } else {
      // For other platforms, just send webhook
      sendAccountInfoToWebhook(platform)
        .catch(err => {
          setError(err instanceof Error ? err.message : 'Failed to send account info to webhook');
        });
    }
  };

  const sendAccountInfoToWebhook = async (platform: string, accountData?: any): Promise<void> => {
    try {
      const webhookUrl = "https://hook.eu2.make.com/bywdxce4ajv8wqld2snukeiy7b4u6bcp";
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get social account if it exists
      const { data: socialAccount } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('user_id', user.id)
        .eq('platform', platform)
        .single();

      const payload = {
        timestamp: new Date().toISOString(),
        event: "connect_platform",
        platform,
        account: accountData || socialAccount || null,
        user: {
          id: user?.id,
          email: user?.email,
          full_name: user?.user_metadata?.full_name
        },
        account_status: {
          platform,
          connected: !!accountData || !!socialAccount?.connected,
          connection_attempt: true
        },
        environment: {
          origin: window.location.origin,
          userAgent: navigator.userAgent
        }
      };

      console.log('Sending webhook payload:', payload);

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Webhook request failed: ${response.status} ${response.statusText}`);
      }

      // Store webhook info for popup
      setWebhookInfo({
        sent: true,
        data: payload
      });
      
      setSuccessMessage('Webhook sent successfully! Check the webhook info popup for details.');

    } catch (err) {
      console.error('Error sending account info to webhook:', err);
      setError(err instanceof Error ? err.message : 'Failed to send account info to webhook');
      throw err;
    }
  };

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      setError(null);
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) throw error;
      
      // Clear all local storage data
      localStorage.clear();
      sessionStorage.clear();
      
      // Show success message
      toast.success('Logged out successfully');
      
      // Redirect to login page
      navigate('/login');
    } catch (err) {
      console.error('Error logging out:', err);
      setError(err instanceof Error ? err.message : 'Failed to log out. Please try again.');
      setLogoutLoading(false);
      setShowLogoutConfirm(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Manage your account settings and preferences
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center space-x-2">
          <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
          <p className="text-sm text-green-600 dark:text-green-300">{successMessage}</p>
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Account Settings</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          <section>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Profile Settings
            </h3>
            <div className="mb-6">
              <div className="flex items-center">
                <div className="relative">
                  <div className="w-20 h-20 overflow-hidden">
                    <AvatarDisplay
                      avatarData={avatarData}
                      size="xl"
                      fallbackText={name}
                      className="border-2 border-indigo-100 dark:border-indigo-800/30"
                    />
                  </div>
                  <label
                    htmlFor="profile-picture"
                    className="absolute bottom-0 right-0 p-1 bg-indigo-600 text-white rounded-full cursor-pointer hover:bg-indigo-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </label>
                  <input
                    type="file"
                    id="profile-picture"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                  />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Profile Picture</p>
                  <div className="flex flex-col space-y-2 mt-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Upload a profile picture (max 5MB)
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowAvatarModal(true)}
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center"
                    >
                      <Brush className="h-3 w-3 mr-1" />
                      Create Custom Avatar
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1 (555) 555-5555"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Optional: Add your phone number for enhanced account security
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Account & Security
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setError(null);
                      setSuccessMessage(null);
                      setLoading(true);

                      if (!newPassword) {
                        throw new Error('Please enter a new password');
                      }

                      if (newPassword !== confirmPassword) {
                        throw new Error('Passwords do not match');
                      }

                      const { error: passwordError } = await supabase.auth.updateUser({
                        password: newPassword
                      });

                      if (passwordError) throw passwordError;

                      setSuccessMessage('Password updated successfully');
                      setNewPassword('');
                      setConfirmPassword('');
                    } catch (err) {
                      
                      setError(err instanceof Error ? err.message : 'Failed to update password');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !newPassword || !confirmPassword}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                    loading || !newPassword || !confirmPassword ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="2fa"
                    type="checkbox"
                    checked={twoFactorEnabled}
                    onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="2fa" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                    Enable Two-Factor Authentication (2FA)
                  </label>
                </div>
                <button
                  type="button"
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                  onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
                >
                  Configure
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Two-factor authentication adds an extra layer of security to your account by requiring
                a verification code in addition to your password.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Palette className="h-5 w-5 mr-2" />
              Theme & Layout
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Theme</label>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setTheme('light')}
                    
                    className={`px-4 py-2 rounded-md ${
                      theme === 'light'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                    }`}
                  >
                    Light
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme('dark')}
                    className={`px-4 py-2 rounded-md ${
                      theme === 'dark'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                    }`}
                  >
                    Dark
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="accentColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Theme Color
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { name: 'Indigo', value: 'indigo', class: 'bg-indigo-500 dark:bg-indigo-600' },
                    { name: 'Blue', value: 'blue', class: 'bg-blue-500 dark:bg-blue-600' },
                    { name: 'Purple', value: 'purple', class: 'bg-purple-500 dark:bg-purple-600' },
                    { name: 'Green', value: 'green', class: 'bg-green-500 dark:bg-green-600' },
                    { name: 'Rose', value: 'rose', class: 'bg-rose-500 dark:bg-rose-600' },
                    { name: 'Amber', value: 'amber', class: 'bg-amber-500 dark:bg-amber-600' }
                  ].map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setAccentColor(color.value as AccentColor)}
                      className={`w-8 h-8 rounded-full transition-all ${color.class} ${
                        accentColor === color.value 
                          ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-600' 
                          : 'hover:scale-110'
                      }`}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center">
                <input
                  id="advancedLayout"
                  type="checkbox"
                  checked={advancedLayout}
                  onChange={(e) => setAdvancedLayout(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="advancedLayout" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Enable advanced widget layout customization
                </label>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Globe className="h-5 w-5 mr-2" />
              Language & Region
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Language
                </label>
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              <div>
                <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Date Format
                </label>
                <select
                  id="dateFormat"
                  value={dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Timezone
                </label>
                <select
                  id="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                </select>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Social Media Integrations</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => socialAccounts.twitter ? handleDisconnectTwitter() : handleConnect('twitter')}
                type="button"
                disabled={twitterConnecting}
                className={`flex items-center space-x-3 p-4 border border-gray-200 dark:border-dark-700 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all ${
                  twitterConnecting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Twitter className="h-5 w-5 text-[#1DA1F2]" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {twitterConnecting ? 'Connecting...' : socialAccounts.twitter ? 'Disconnect Twitter (X)' : 'Connect Twitter (X)'}
                </span>
                {socialAccounts.twitter && (
                  <span className="ml-auto px-2 py-1 text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-full">
                    Connected
                  </span>
                )}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleConnect('facebook')}
                type="button"
                className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-dark-700 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
              >
                <Facebook className="h-5 w-5 text-[#1877F2]" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Connect Facebook</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleConnect('instagram')}
                type="button"
                className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-dark-700 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
              >
                <Instagram className="h-5 w-5 text-[#E4405F]" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Connect Instagram</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleConnect('youtube')} 
                type="button"
                disabled={loading}
                className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-dark-700 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
              >
                <Youtube className="h-5 w-5 text-[#FF0000]" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {socialAccounts.youtube ? 'Disconnect YouTube' : 'Connect YouTube'}
                </span>
                {socialAccounts.youtube && (
                  <span className="ml-auto px-2 py-1 text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-full">
                    Connected
                  </span>
                )}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => socialAccounts.linkedin ? handleDisconnectLinkedIn() : handleConnect('linkedin')}
                type="button"
                className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-dark-700 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
              >
                <Linkedin className="h-5 w-5 text-[#0A66C2]" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Connect LinkedIn</span>
                {socialAccounts.linkedin && (
                  <span className="ml-auto px-2 py-1 text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-full">
                    Connected
                  </span>
                )}
              </motion.button>
            </div>
            
          </section>

          <section>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Bell className="h-5 w-5 mr-2" />
              Notification Preferences
            </h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="emailUpdates"
                  type="checkbox"
                  checked={notifications.emailUpdates}
                  onChange={(e) => setNotifications(prev => ({
                    ...prev,
                    emailUpdates: e.target.checked
                  }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="emailUpdates" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Email Updates
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="contentAlerts"
                  type="checkbox"
                  checked={notifications.contentAlerts}
                  onChange={(e) => setNotifications(prev => ({
                    ...prev,
                    contentAlerts: e.target.checked
                  }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="contentAlerts" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Content Alerts
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="performanceReports"
                  type="checkbox"
                  checked={notifications.performanceReports}
                  onChange={(e) => setNotifications(prev => ({
                    ...prev,
                    performanceReports: e.target.checked
                  }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="performanceReports" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Performance Reports
                </label>
              </div>
            </div>
          </section>

          <div className="flex justify-end">
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(true)}
                className="inline-flex items-center px-4 py-2 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md text-red-700 dark:text-red-400 bg-white dark:bg-dark-800 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Webhook Info Popup */}
      {webhookInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-lg w-full"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Webhook Information</h3>
                <button
                  onClick={() => setWebhookInfo(null)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                  <CheckCircle className="h-5 w-5" />
                  <span>Webhook sent successfully</span>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Data Sent:</h4>
                  <pre className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4 text-xs overflow-auto max-h-60">
                    {JSON.stringify(webhookInfo.data, null, 2)}
                  </pre>
                </div>

                <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                  <p className="text-sm text-indigo-700 dark:text-indigo-300">
                    This information has been sent to Make.com for processing. You can check your Make.com scenario for the complete workflow execution details.
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setWebhookInfo(null)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Avatar Customization Modal */}
      <AvatarModal
        isOpen={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onSave={handleSaveAvatar}
        initialAvatar={avatarData}
      />
      
      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-md w-full"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Confirm Logout</h3>
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-400">
                  Are you sure you want to log out? You will need to sign in again to access your account.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  disabled={logoutLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors flex items-center"
                >
                  {logoutLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white\" fill="none\" viewBox="0 0 24 24">
                        <circle className="opacity-25\" cx="12\" cy="12\" r="10\" stroke="currentColor\" strokeWidth="4" />
                        <path className="opacity-75\" fill="currentColor\" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Logging out...
                    </>
                  ) : (
                    <>
                      <LogOut className="h-4 w-4 mr-2" />
                      Confirm Logout
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default Settings;